<template>
    <v-form class="pb-4" data-cy="description-check-in-out-form">
        <v-row dense>
            <v-col cols="12" md="2">
                <span class="text-h6 text--secondary">
                    {{ $t('product_tabs.check_in') }}
                </span>
            </v-col>
            <v-col cols="12" md="10">
                <div class="text-subtitle-1 pb-2">{{ $t('check_in') }} (HH:mm)</div>
                <v-row dense class="mb-4">
                    <v-col class="flex-grow-0">
                        <v-text-field
                            v-model="formData.checkInFrom"
                            :label="$t('after')"
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="check-in-from"
                            maxlength="5"
                            hide-details
                            :rules="[timeFormatRule()]"
                            width="120px"
                            :readonly="isHotelDescriptionMode"
                            @update:model-value="e => formatTime(e, 'checkInFrom')"
                        />
                    </v-col>
                    <v-col class="flex-grow-0">
                        <v-text-field
                            v-model="formData.checkInTo"
                            :label="$t('before')"
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="check-in-to"
                            maxlength="5"
                            hide-details
                            :rules="[timeFormatRule()]"
                            width="120px"
                            :readonly="isHotelDescriptionMode"
                            @update:model-value="e => formatTime(e, 'checkInTo')"
                        />
                    </v-col>
                </v-row>
                <div class="text-subtitle-1 pb-2">{{ $t('check_out') }} (HH:mm)</div>
                <v-row dense class="mb-4">
                    <v-col class="flex-grow-0">
                        <v-text-field
                            v-model="formData.checkOutFrom"
                            :label="$t('after')"
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="check-out-from"
                            maxlength="5"
                            hide-details
                            :rules="[timeFormatRule()]"
                            width="120px"
                            :readonly="isHotelDescriptionMode"
                            @update:model-value="e => formatTime(e, 'checkOutFrom')"
                        />
                    </v-col>
                    <v-col class="flex-grow-0">
                        <v-text-field
                            v-model="formData.checkOutTo"
                            :label="$t('before')"
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="check-out-to"
                            maxlength="5"
                            hide-details
                            :rules="[timeFormatRule()]"
                            width="120px"
                            :readonly="isHotelDescriptionMode"
                            @update:model-value="e => formatTime(e, 'checkOutTo')"
                        />
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row no-gutters class="gc-4">
            <v-btn v-if="!isHotelDescriptionMode" data-cy="save" :disabled="isLoading" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn
                class="cancel-btn"
                data-cy="cancel"
                :disabled="isLoading"
                :to="{name: 'supplierProducts'}"
                variant="text"
            >
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import {timeFormatRule} from '~/composables/rules'

    const isLoading = ref<boolean>(false)
    const formData = ref({
        checkInFrom: '',
        checkInTo: '',
        checkOutFrom: '',
        checkOutTo: '',
        nextDayCheckout: false,
    })
    const isHotelDescriptionMode: boolean = inject('isHotelDescriptionMode', false)
    const hotelDescriptionId: string = inject('hotelDescriptionId', null)

    const app = useNuxtApp()
    const {t} = useI18n()

    watch(useHotelInfo, async () => {
        await loadData()
    })

    onMounted(async () => {
        if (supplierStore.fullContract?.hotelContentId || isHotelDescriptionMode) await loadData()
    })

    async function loadData(): Promise<void> {
        const hotelId = isHotelDescriptionMode ? hotelDescriptionId : supplierStore.fullContract.hotelContentId
        try {
            isLoading.value = true
            formData.value = await app.$api.hotelDescriptionCheckin.get(hotelId)
        } catch (e) {
            app.$toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            const hotelId = isHotelDescriptionMode ? hotelDescriptionId : supplierStore.fullContract.hotelContentId
            await app.$api.hotelDescriptionCheckin.put(hotelId, formData.value)

            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const formatTime = (event, key): void => {
        const filteredValue = event.replace(/[^0-9:]/g, '')
        const formattedValue = filteredValue.replace(/(\d{2})(?=\d)/g, '$1:')
        formData.value[key] = formattedValue
    }
</script>
